import { orderXClient } from '@/service/client'
import {
  AcceptRfqQuoteRequest,
  AcceptRfqQuoteResponse,
  CancelRfqMakerQuoteRequest,
  CancelRfqMakerQuoteResponse,
  CancelRfqRequest,
  CancelRfqResponse,
  CreateRfqRequest,
  CreateRfqResponse,
  GetRfqHistoryRequest,
  GetRfqHistoryResponse,
  GetRfqMakersRequest,
  GetRfqMakersResponse,
  RfqAnalytics,
  RfqAnalyticsRequest,
  SendRfqMakerQuoteRequest,
  SendRfqMakerQuoteResponse,
  ServerMessage,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

/**
 * RFQ command/query endpoints. All are authenticated (JWT + account access) and use
 * the same protobuf `/api` transport as the rest of the service layer: send an
 * `ApiRequest` oneof, read the matching `ServerMessage` response variant.
 *
 * Command responses are dispatch ACKs, not RFQ state — drive UI from the RFQ stream.
 */

export async function createRfq(payload: CreateRfqRequest): Promise<CreateRfqResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { createRfq: payload })
    return data.createRfqResponse as CreateRfqResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function cancelRfq(payload: CancelRfqRequest): Promise<CancelRfqResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { cancelRfq: payload })
    return data.cancelRfqResponse as CancelRfqResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function acceptRfqQuote(
  payload: AcceptRfqQuoteRequest
): Promise<AcceptRfqQuoteResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { acceptRfqQuote: payload })
    return data.acceptRfqQuoteResponse as AcceptRfqQuoteResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function getRfqMakers(payload: GetRfqMakersRequest): Promise<GetRfqMakersResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getRfqMakers: payload })
    return data.getRfqMakersResponse as GetRfqMakersResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function getRfqHistory(payload: GetRfqHistoryRequest): Promise<GetRfqHistoryResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getRfqHistory: payload })
    return data.getRfqHistoryResponse as GetRfqHistoryResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * RFQ analytics (ReadOnly). `rfqId` set = single-RFQ detail; unset = aggregate window.
 * The response rides `ServerMessage.data.rfq_analytics` (like exec/vol analytics),
 * not a typed `*_response`.
 */
export async function fetchRfqAnalytics(payload: RfqAnalyticsRequest): Promise<RfqAnalytics> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { rfqAnalytics: payload })
    if (!data.data?.rfqAnalytics) return Promise.reject(new Error('No RFQ analytics data'))
    return data.data.rfqAnalytics
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Maker quote send / re-price. The response means "routed", not "live": the quote appears
 * on the RFQ_MAKER stream as OPEN or REJECTED. `quoteId` set = upsert that OPEN quote; a
 * terminal quote_id is refused, so a new quote after EXPIRED etc. omits it
 * (see `resolveSendQuoteId`).
 */
export async function sendRfqMakerQuote(
  payload: SendRfqMakerQuoteRequest
): Promise<SendRfqMakerQuoteResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { sendRfqMakerQuote: payload })
    return data.sendRfqMakerQuoteResponse as SendRfqMakerQuoteResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/** Maker quote cancel. `accepted` means routed; the quote moves to CANCELLED on the stream. */
export async function cancelRfqMakerQuote(
  payload: CancelRfqMakerQuoteRequest
): Promise<CancelRfqMakerQuoteResponse> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      cancelRfqMakerQuote: payload,
    })
    return data.cancelRfqMakerQuoteResponse as CancelRfqMakerQuoteResponse
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}
